var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { inject, injectable } from 'inversify';
import { serialize } from 'object-to-formdata';
import { Logger } from '../logger';
import { QueryService } from '../query';
import { UpdatorService } from '../updator';
import { DegradationService } from '../degradation';
import { AppError } from '../error';
import { RoleService, TokenService, LocaleService } from './types';
import { HTTP_NOT_UNAUTHORIZED_STATUS } from './codes';
const RELOAD_DELAY_AFTER_REQUEST = 400;
const DEGRADED_HEADER = 'X-System-Degraded';
const DEGRADED_HEADER_VALUE = '1';
const retryOn = (status) => {
    return (target, key, descriptor) => {
        const originalMethod = descriptor.value;
        descriptor.value = function (resource_1) {
            return __awaiter(this, arguments, void 0, function* (resource, options = {}) {
                let response = yield originalMethod.apply(this, [resource, options]);
                if (response.status !== status) {
                    return response;
                }
                const prevToken = target.getToken.apply(this);
                yield target.restoreToken.apply(this);
                const nextToken = target.getToken.apply(this);
                if (prevToken !== nextToken) {
                    response = yield originalMethod.apply(this, [
                        resource,
                        Object.assign(Object.assign({}, options), { headers: Object.assign(Object.assign({}, options.headers), { Authorization: `Bearer ${nextToken}` }) }),
                    ]);
                }
                return response;
            });
        };
        return descriptor;
    };
};
let HttpService = class HttpService {
    // eslint-disable-next-line max-params
    constructor(query, token, role, translationLocaleService, logger, updator, degradation) {
        this.query = query;
        this.token = token;
        this.role = role;
        this.translationLocaleService = translationLocaleService;
        this.logger = logger;
        this.updator = updator;
        this.degradation = degradation;
        this.host = window.REACT_APP_VKHRTEK_API_URL;
        this.numberRequests = 0;
        this.requiredReloadUrlExists = false;
    }
    /**
     * Читает body ответа (которое в виде текста JSON-формата) и преобразует к объекту
     *
     * @param response - Интерфейс Fetch API представляет собой ответ на запрос
     * @returns Объект с данными ответа или пустой объект при ошибке парсинга
     */
    parseJson(response) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield response.json();
            }
            catch (_a) {
                return {};
            }
        });
    }
    /**
     * Расширить объект ответа контекстным значением
     * Для расширения используется Proxy
     *
     * @param response - Проксируемый/расширяемый объект
     * @param context - Контекстный объект
     * @returns Проксированный объект с добавленным свойством __context__
     */
    extendResponse(response, context) {
        return new Proxy(response, {
            get(target, prop) {
                if (prop === '__context__') {
                    return context;
                }
                return target[prop];
            },
        });
    }
    /**
     * Базовый метод для выполнения запросов к бэкенду
     * Является оберткой над браузерным fetch
     * Позволяет прервать запрос по истечении timeout
     * Управляет принудительным обновлением страницы в браузере при помощи reloadIfOldVersion из опций
     *
     * @param resource - Адрес ресурса
     * @param options - Опции, среди которых часто встречаются body, headers, method, timeout, reloadIfOldVersion
     * @param log - Дополнительные данные для логирования
     * @returns Ответ сервера в виде Response
     */
    fetch(resource_1) {
        return __awaiter(this, arguments, void 0, function* (resource, options = {}, log = {}) {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            const { timeout = 0, reloadIfOldVersion = false } = options;
            const controller = new AbortController();
            const id = setTimeout(() => {
                var _a, _b;
                if (timeout) {
                    controller.abort();
                    this.logger.info(`[http] [CANCELED BY TIMEOUT] ${((_a = options.method) === null || _a === void 0 ? void 0 : _a.toUpperCase()) || 'GET'} ${resource.split('?')[0]}`, {
                        tags: {
                            vkdoc_error_type: 'http',
                            http_method: ((_b = options.method) === null || _b === void 0 ? void 0 : _b.toUpperCase()) || 'GET',
                            http_url: resource.split('?')[0],
                            http_status: 'canceled',
                        },
                        context: {
                            http_query: resource.split('?')[1]
                                ? { value: resource.split('?')[1] }
                                : null,
                        },
                    });
                }
            }, timeout);
            this.numberRequests++;
            if (reloadIfOldVersion) {
                this.requiredReloadUrlExists = true;
            }
            const response = yield fetch(resource, Object.assign(Object.assign({}, options), { signal: controller.signal }));
            clearTimeout(id);
            const version = (_a = response.headers) === null || _a === void 0 ? void 0 : _a.get('X-App-Version');
            if (version) {
                this.updator.updateVersion(version);
            }
            if (((_b = response.headers) === null || _b === void 0 ? void 0 : _b.get(DEGRADED_HEADER)) === DEGRADED_HEADER_VALUE) {
                this.degradation.markDegraded();
            }
            this.numberRequests--;
            setTimeout(() => {
                if (this.numberRequests === 0 && this.requiredReloadUrlExists) {
                    this.updator.run();
                }
            }, RELOAD_DELAY_AFTER_REQUEST);
            if (!response.ok) {
                let errorResponse = {};
                try {
                    errorResponse = yield response.clone().json();
                }
                catch (e) { }
                const traceId = response.headers.get('X-Mayday-Trace-Id');
                this.logger.info(`[http] [${response.status}] ${((_c = options.method) === null || _c === void 0 ? void 0 : _c.toUpperCase()) || 'GET'} ${resource.split('?')[0]}`, {
                    tags: {
                        vkdoc_error_type: 'http',
                        http_method: ((_d = options.method) === null || _d === void 0 ? void 0 : _d.toUpperCase()) || 'GET',
                        http_status: response.status,
                        http_url: resource.split('?')[0],
                        http_trace_id: traceId || 'empty',
                        http_error_code: ((_e = errorResponse === null || errorResponse === void 0 ? void 0 : errorResponse.data) === null || _e === void 0 ? void 0 : _e.error_code) || 'empty',
                        http_side: ((_f = options.headers) === null || _f === void 0 ? void 0 : _f['X-Side']) || 'empty',
                        http_version: ((_g = options.headers) === null || _g === void 0 ? void 0 : _g['X-App-Version']) || 'empty',
                    },
                    context: {
                        http_body: log.body
                            ? {
                                value: log.body,
                            }
                            : null,
                        http_query: resource.split('?')[1]
                            ? { value: resource.split('?')[1] }
                            : null,
                        http_response: {
                            value: errorResponse,
                        },
                    },
                });
                throw new AppError('server', {
                    code: errorResponse.code || response.status,
                    message: (_h = errorResponse.message) !== null && _h !== void 0 ? _h : '',
                    data: errorResponse.data,
                }, traceId);
            }
            return response;
        });
    }
    /**
     * Обновить токен
     */
    restoreToken() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.token.restoreToken();
        });
    }
    /**
     * Получить токен
     * Если токен отсутствует, выбросить исключение
     *
     * @returns Токен авторизации
     */
    getToken() {
        const token = this.token.get();
        if (!token) {
            throw new AppError('client', {
                code: 401,
                message: 'Unauthorized',
                error: 'Unauthorized',
            });
        }
        return token;
    }
    /**
     * Получить роль
     * Допустимые роли: employee (сотрудник компании) и company (представитель со стороны компании)
     *
     * @returns Роль пользователя
     */
    getRole() {
        const role = this.role.get();
        if (!role) {
            return 'employee';
        }
        return role;
    }
    isValidUrl(url) {
        try {
            new URL(url);
            return true;
        }
        catch (error) {
            return false;
        }
    }
    /**
     * Сформировать объект заголовков, которые будут отправлены с запросом
     *
     * @param options - Параметры формирования заголовков
     * @returns Объект заголовков
     */
    createHeaders({ withAuth, withSide, isJson, role, }) {
        const headers = isJson
            ? {
                'Content-Type': 'application/json',
            }
            : {};
        if (withAuth) {
            headers.Authorization = `Bearer ${this.getToken()}`;
        }
        if (withSide) {
            headers['X-Side'] = role || this.getRole();
        }
        headers['Accept-Language'] = this.translationLocaleService.get();
        return headers;
    }
    /**
     * Сформировать тело запроса
     *
     * @param data - Данные для отправки
     * @param toJson - Определяет требуется ли преобразовать объект данных к JSON-формату
     * @param withIndicies - Флаг включения индексов при сериализации в FormData
     * @returns Тело запроса в виде строки JSON или FormData
     */
    createBody(data, toJson = true, withIndicies = false) {
        if (toJson) {
            return data ? JSON.stringify(data) : '';
        }
        return serialize(data, { indices: withIndicies });
    }
    /**
     * Сформировать строку запроса
     *
     * @param url - Базовый URL
     * @param query - Объект параметров запроса
     * @returns Строка запроса (query string)
     */
    createQuery(url, query) {
        if (!(query && Object.keys(query).length)) {
            return '';
        }
        const symbol = url.includes('?') ? '&' : '?';
        return `${symbol}${this.query.stringify(query)}`;
    }
    /**
     * Выполнить GET запрос
     *
     * @param url - URL ресурса
     * @param query - Параметры запроса
     * @param options - Опции запроса
     * @returns Ответ сервера в виде объекта или null
     */
    get(url_1, query_1) {
        return __awaiter(this, arguments, void 0, function* (url, query, { withAuth = true, withSide = false, isJson = true, timeout = 0, reloadIfOldVersion = false, role, } = {}) {
            const headers = this.createHeaders({
                withAuth,
                withSide,
                isJson,
                role,
            });
            const response = yield this.fetch(`${url.startsWith('http') ? '' : this.host}${url}${this.createQuery(url, query)}`, {
                headers,
                timeout,
                reloadIfOldVersion,
            });
            return isJson
                ? this.extendResponse(yield this.parseJson(response), {
                    vkdoc_error_type: 'validation',
                    http_url: url,
                    http_method: 'GET',
                    http_trace_id: response.headers.get('X-Mayday-Trace-Id'),
                    http_side: headers['X-Side'] || 'empty',
                    http_version: headers['X-App-Version'] || 'empty',
                })
                : null;
        });
    }
    /**
     * Выполнить запрос на загрузку файла с сервера
     *
     * @param url - URL ресурса
     * @param options - Опции запроса
     * @param query - Параметры запроса
     * @returns Объект с файлом и именем файла
     */
    getFile(url_1) {
        return __awaiter(this, arguments, void 0, function* (url, { withAuth = true, withSide = false, isJson = false, timeout = 0, role, } = {}, query) {
            var _a;
            const headers = this.createHeaders({
                withAuth,
                withSide,
                isJson,
                role,
            });
            const fullUrl = this.isValidUrl(url) ? url : `${this.host}${url}`;
            const response = yield this.fetch(`${fullUrl}${query && Object.keys(query).length
                ? `?${this.query.stringify(query)}`
                : ''}`, {
                headers,
                timeout,
            });
            const contentDisposition = response &&
                response.headers &&
                response.headers.get('Content-Disposition');
            const decodedContentDisposition = (_a = decodeURI(contentDisposition || '')) === null || _a === void 0 ? void 0 : _a.replace(/\*|UTF-8|\"|\'\'/g, '');
            const filename = ((decodedContentDisposition === null || decodedContentDisposition === void 0 ? void 0 : decodedContentDisposition.match(/filename=(.+)/)) ||
                [])[1];
            return { file: response.blob(), filename: filename };
        });
    }
    /**
     * Загрузить файл на сервер
     *
     * @param url - URL ресурса
     * @param data - Данные для отправки
     * @param options - Опции запроса
     * @returns Blob с файлом
     */
    generateFile(url_1, data_1) {
        return __awaiter(this, arguments, void 0, function* (url, data, { withAuth = true, withSide = false, method = 'post', withIndicies = false, timeout = 0, isJson = false, role, } = {}) {
            const headers = this.createHeaders({
                withAuth,
                withSide,
                isJson,
                role,
            });
            const response = yield this.fetch(`${this.host}${url}`, {
                method: method.toUpperCase(),
                body: this.createBody(data, isJson, withIndicies),
                headers,
                timeout,
            });
            return response.blob();
        });
    }
    /**
     * Выполнить POST запрос
     *
     * @param url - URL ресурса
     * @param data - Данные для отправки
     * @param options - Опции запроса
     * @returns Ответ сервера в виде объекта или null
     */
    post(url_1, data_1) {
        return __awaiter(this, arguments, void 0, function* (url, data, { withAuth = true, withSide = false, isJson = true, withIndicies = false, timeout = 0, reloadIfOldVersion = false, role, } = {}) {
            const headers = this.createHeaders({
                withAuth,
                withSide,
                isJson,
                role,
            });
            const response = yield this.fetch(`${this.host}${url}`, {
                method: 'POST',
                body: this.createBody(data, isJson, withIndicies),
                headers,
                timeout,
                reloadIfOldVersion,
            });
            try {
                const res = this.extendResponse(yield this.parseJson(response), {
                    vkdoc_error_type: 'validation',
                    http_url: url,
                    http_method: 'POST',
                    http_trace_id: response.headers.get('X-Mayday-Trace-Id'),
                    http_side: headers['X-Side'] || 'empty',
                    http_version: headers['X-App-Version'] || 'empty',
                });
                return res;
            }
            catch (e) {
                return null;
            }
        });
    }
    /**
     * Выполнить PUT запрос
     *
     * @param url - URL ресурса
     * @param data - Данные для отправки
     * @param options - Опции запроса
     * @returns Ответ сервера в виде объекта или null
     */
    put(url_1, data_1) {
        return __awaiter(this, arguments, void 0, function* (url, data, { withAuth = true, withSide = false, isJson = true, withIndicies = false, timeout = 0, role, } = {}) {
            const headers = this.createHeaders({
                withAuth,
                withSide,
                isJson,
                role,
            });
            const response = yield this.fetch(`${this.host}${url}`, {
                method: 'PUT',
                body: this.createBody(data, isJson, withIndicies),
                headers,
                timeout,
            });
            return isJson
                ? this.extendResponse(yield this.parseJson(response), {
                    vkdoc_error_type: 'validation',
                    http_url: url,
                    http_method: 'PUT',
                    http_trace_id: response.headers.get('X-Mayday-Trace-Id'),
                    http_side: headers['X-Side'] || 'empty',
                    http_version: headers['X-App-Version'] || 'empty',
                })
                : null;
        });
    }
    /**
     * Выполнить PATCH запрос
     *
     * @param url - URL ресурса
     * @param data - Данные для отправки
     * @param options - Опции запроса
     * @returns Ответ сервера в виде объекта или null
     */
    patch(url_1, data_1) {
        return __awaiter(this, arguments, void 0, function* (url, data, { withAuth = true, withSide = false, isJson = true, withIndicies = false, timeout = 0, role, } = {}) {
            const headers = this.createHeaders({
                withAuth,
                withSide,
                isJson,
                role,
            });
            const response = yield this.fetch(`${this.host}${url}`, {
                method: 'PATCH',
                body: this.createBody(data, isJson, withIndicies),
                headers,
                timeout,
            });
            return isJson
                ? this.extendResponse(yield this.parseJson(response), {
                    vkdoc_error_type: 'validation',
                    http_url: url,
                    http_method: 'PATCH',
                    http_trace_id: response.headers.get('X-Mayday-Trace-Id'),
                    http_side: headers['X-Side'] || 'empty',
                    http_version: headers['X-App-Version'] || 'empty',
                })
                : null;
        });
    }
    /**
     * Выполнить DELETE запрос
     *
     * @param url - URL ресурса
     * @param data - Данные для отправки
     * @param options - Опции запроса
     * @returns Ответ сервера в виде объекта или null
     */
    delete(url_1, data_1) {
        return __awaiter(this, arguments, void 0, function* (url, data, { withAuth = true, withSide = false, isJson = true, withIndicies = false, timeout = 0, role, } = {}) {
            const headers = this.createHeaders({
                withAuth,
                withSide,
                isJson,
                role,
            });
            const response = yield this.fetch(`${this.host}${url}`, {
                method: 'DELETE',
                body: this.createBody(data, isJson, withIndicies),
                headers,
                timeout,
            });
            return isJson
                ? this.extendResponse(yield this.parseJson(response), {
                    vkdoc_error_type: 'validation',
                    http_url: url,
                    http_method: 'DELETE',
                    http_trace_id: response.headers.get('X-Mayday-Trace-Id'),
                    http_side: headers['X-Side'] || 'empty',
                    http_version: headers['X-App-Version'] || 'empty',
                })
                : null;
        });
    }
};
__decorate([
    retryOn(HTTP_NOT_UNAUTHORIZED_STATUS),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String, Object, Object]),
    __metadata("design:returntype", Promise)
], HttpService.prototype, "fetch", null);
HttpService = __decorate([
    injectable(),
    __param(1, inject(TokenService)),
    __param(2, inject(RoleService)),
    __param(3, inject(LocaleService)),
    __metadata("design:paramtypes", [QueryService, Object, Object, Object, Logger,
        UpdatorService,
        DegradationService])
], HttpService);
export { HttpService };
